import { useEffect, useState } from "react";
import { Wrench, Plus, X, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { JOB_STATUSES } from "@/lib/config";
import { Card, Badge, Btn, EmptyState, AInput, ASelect, ATextarea, useSave } from "../AdminUI";

const statusColor = (s) => ({ Completed: "green", Cancelled: "red", "In Progress": "amber", Scheduled: "blue" }[s] || "gray");

function JobModal({ job, customers, quotes, onClose, onSaved, canDelete }) {
  const [f, setF] = useState(job || { customer_id: "", quote_id: "", property: "", scheduled_date: "", scheduled_time: "", assigned_staff: "", status: "Unscheduled", notes: "", estimated_value: "" });
  const [saving, run] = useSave();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = (e) => { e.preventDefault(); run(async () => {
    const body = { ...f, estimated_value: f.estimated_value ? parseFloat(f.estimated_value) : null };
    if (job) await api.patch(`/admin/jobs/${job.id}`, body); else await api.post("/admin/jobs", body);
    onSaved(); onClose();
  }, "Job saved"); };
  const del = () => { if (!window.confirm("Delete this job?")) return; run(async () => { await api.delete(`/admin/jobs/${job.id}`); onSaved(); onClose(); }, "Deleted"); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="job-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <form onSubmit={submit} className="relative bg-white rounded-2xl w-full max-w-xl p-6 space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="font-head font-bold text-xl text-[var(--navy)] uppercase">{job ? `Job ${job.job_id}` : "New Job"}</h2>
          <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <ASelect label="Customer" value={f.customer_id} options={[{ value: "", label: "— Select —" }, ...customers.map((c) => ({ value: c.id, label: `${c.name} (${c.customer_id})` }))]} onChange={set("customer_id")} data-testid="job-customer" />
          <ASelect label="Linked Quote" value={f.quote_id} options={[{ value: "", label: "— None —" }, ...quotes.map((q) => ({ value: q.id, label: `${q.quote_id} ($${q.total})` }))]} onChange={set("quote_id")} />
          <AInput label="Property / Address" value={f.property} onChange={set("property")} />
          <ASelect label="Status" value={f.status} options={JOB_STATUSES} onChange={set("status")} data-testid="job-status" />
          <AInput label="Scheduled Date" type="date" value={f.scheduled_date} onChange={set("scheduled_date")} data-testid="job-date" />
          <AInput label="Scheduled Time" type="time" value={f.scheduled_time} onChange={set("scheduled_time")} />
          <AInput label="Assigned Staff" value={f.assigned_staff} onChange={set("assigned_staff")} />
          <AInput label="Estimated Value ($)" type="number" value={f.estimated_value || ""} onChange={set("estimated_value")} />
        </div>
        <ATextarea label="Notes" value={f.notes} onChange={set("notes")} />
        <div className="flex gap-2 justify-between">
          {job && canDelete ? <Btn variant="danger" type="button" onClick={del}><Trash2 className="w-4 h-4" /> Delete</Btn> : <span />}
          <div className="flex gap-2">
            <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
            <Btn type="submit" loading={saving} data-testid="job-save">Save Job</Btn>
          </div>
        </div>
      </form>
    </div>
  );
}

export default function Jobs({ canDelete }) {
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [edit, setEdit] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const load = () => api.get("/admin/jobs").then((r) => setJobs(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {}); api.get("/admin/quotes").then((r) => setQuotes(r.data)).catch(() => {}); }, []);
  const custName = (id) => customers.find((c) => c.id === id)?.name || "—";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-head font-extrabold text-3xl text-[var(--navy)] uppercase tracking-tight">Jobs</h1>
        <Btn onClick={() => setShowNew(true)} data-testid="new-job-btn"><Plus className="w-4 h-4" /> New Job</Btn>
      </div>
      <Card>
        {jobs.length === 0 ? <EmptyState icon={Wrench} title="No jobs yet" hint="Schedule a job for a customer." /> : (
          <div className="overflow-x-auto -m-1">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-400 text-xs uppercase border-b border-gray-100">
                <th className="py-2 px-2">Job</th><th className="px-2">Customer</th><th className="px-2">Scheduled</th><th className="px-2">Staff</th><th className="px-2">Status</th>
              </tr></thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id} onClick={() => setEdit(j)} className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer" data-testid={`job-row-${j.job_id}`}>
                    <td className="py-3 px-2 font-semibold text-[var(--navy)]">{j.job_id}</td>
                    <td className="px-2 text-gray-600">{custName(j.customer_id)}</td>
                    <td className="px-2 text-gray-600">{j.scheduled_date || "—"} {j.scheduled_time}</td>
                    <td className="px-2 text-gray-600">{j.assigned_staff || "—"}</td>
                    <td className="px-2"><Badge color={statusColor(j.status)}>{j.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {showNew && <JobModal customers={customers} quotes={quotes} onClose={() => setShowNew(false)} onSaved={load} canDelete={canDelete} />}
      {edit && <JobModal job={edit} customers={customers} quotes={quotes} onClose={() => setEdit(null)} onSaved={load} canDelete={canDelete} />}
    </div>
  );
}
